import { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { locale } from "@/lib/format";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAppBase } from "@/hooks/useAppBase";
import { toast } from "sonner";
import { fmtDate } from "@/lib/formatDate";
import SortPopover from "@/components/SortPopover";
import {
  Plus, X, Mail, Phone, Search, Filter, Cake, ChevronRight,
  Tag as TagIcon, Download, Upload, ArrowUp, ArrowDown, ArrowUpDown,
  ChevronDown, Trash2, Move, Check, Users as UsersIcon,
} from "lucide-react";

// 5 fonti contatti — allineate con le pipeline auto-sources del backend
// (routes/pipeline.py::AVAILABLE_PIPELINE_SOURCES). Le legacy vengono
// mappate via SOURCE_LABEL sotto per continuità con dati storici.
// Fonti contatto — richieste dall'analytics del titolare per tracciare
// automaticamente il funnel di acquisizione. Le prime 5 restano allineate
// alle pipeline auto-sources del backend (routes/pipeline.py). Co-Marketing,
// Eventi e Altro sono nuove opzioni tracciate solo lato analytics.
const SOURCES = [
  { value: "referral", label: "Passaparola" },
  { value: "social", label: "Social" },
  { value: "ads_facebook_form", label: "ADS Facebook form" },
  { value: "co_marketing", label: "Co-Marketing" },
  { value: "eventi", label: "Eventi" },
  { value: "website", label: "Sito web" },
  { value: "walk-in", label: "Passaggio" },
  { value: "altro", label: "Altro" },
];
const SOURCE_LABEL = {
  ...Object.fromEntries(SOURCES.map((s) => [s.value, s.label])),
  // Legacy source values on existing contacts — display friendly labels
  ads: "ADS Facebook form", ads_facebook: "ADS Facebook form",
  ads_instagram: "ADS Facebook form", lead_ads: "ADS Facebook form",
  facebook: "Social", instagram: "Social",
  web_form: "Sito web", web_lead: "Sito web", landing_page: "Sito web",
  google: "Sito web", import: "Import", walkin: "Passaggio",
};

export default function Clients() {
  const { t } = useTranslation();
  const base = useAppBase();
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [query, setQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [pipelineFilter, setPipelineFilter] = useState("");
  const [tagFilter, setTagFilter] = useState([]);
  const [dateField, setDateField] = useState("created_at");
  const [dateSince, setDateSince] = useState("");
  const [dateUntil, setDateUntil] = useState("");
  // GHL-style Sort: field + direction. Field is any key of the client doc.
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);

  function emptyForm() {
    return { name: "", email: "", phone: "", notes: "", tags: [], birthday: "" };
  }

  const load = async () => {
    const [c, t, ps] = await Promise.all([
      api.get("/clients"),
      api.get("/tags"),
      api.get("/pipeline/stages").catch(() => ({ data: [] })),
    ]);
    setItems(c.data);
    setTags(t.data);
    setPipelineStages(ps.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      email: form.email || null,
      birthday: form.birthday || null,
    };
    try {
      await api.post("/clients", payload);
      toast.success(t("clients.created"));
      setShowForm(false);
      setForm(emptyForm());
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const toggleTagFilter = (name) => {
    setTagFilter((prev) => prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = items.filter((c) => {
      if (sourceFilter && c.source !== sourceFilter) return false;
      if (pipelineFilter && c.pipeline_stage !== pipelineFilter) return false;
      if (tagFilter.length && !tagFilter.every((t) => (c.tags || []).includes(t))) return false;
      if (dateSince || dateUntil) {
        const v = c[dateField] || "";
        if (dateSince && v < dateSince) return false;
        if (dateUntil && v > `${dateUntil}T23:59:59.999Z`) return false;
      }
      if (q) {
        const hay = `${c.name} ${c.email || ""} ${c.phone || ""} ${c.notes || ""} ${(c.tags || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      const av = a[sortField] ?? "";
      const bv = b[sortField] ?? "";
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), "it", { numeric: true }) * dir;
    };
    out.sort(cmp);
    return out;
  }, [items, query, sourceFilter, pipelineFilter, tagFilter, dateField, dateSince, dateUntil, sortField, sortDir]);

  const tagColor = (name) => tags.find((t) => t.name === name)?.color || "#06B6D4";

  const upcomingBirthdays = useMemo(() => {
    const now = new Date();
    const list = [];
    for (const c of items) {
      if (!c.birthday) continue;
      const [, m, d] = c.birthday.split("-").map(Number);
      if (!m || !d) continue;
      let next = new Date(now.getFullYear(), m - 1, d);
      if (next < new Date(now.getFullYear(), now.getMonth(), now.getDate())) next = new Date(now.getFullYear() + 1, m - 1, d);
      const days = Math.ceil((next - now) / (1000 * 60 * 60 * 24));
      if (days <= 3) list.push({ ...c, days });
    }
    return list.sort((a, b) => a.days - b.days).slice(0, 5);
  }, [items]);

  const exportCsv = async () => {
    // Applica filtri lato server (per esportare anche più record)
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (sourceFilter) params.set("source", sourceFilter);
    if (pipelineFilter) params.set("pipeline_stage", pipelineFilter);
    tagFilter.forEach((t) => params.append("tags", t));
    if (dateSince) {
      params.set(dateField === "pipeline_updated_at" ? "pipeline_moved_since"
                 : dateField === "updated_at" ? "updated_since" : "created_since", dateSince);
    }
    if (dateUntil) {
      params.set(dateField === "pipeline_updated_at" ? "pipeline_moved_until"
                 : dateField === "updated_at" ? "updated_until" : "created_until", dateUntil);
    }
    try {
      const r = await api.get(`/clients/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contatti_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("clients.exportStarted"));
    } catch (e) {
      toast.error(t("clients.exportError"));
    }
  };

  const resetFilters = () => {
    setQuery(""); setSourceFilter(""); setPipelineFilter(""); setTagFilter([]);
    setDateSince(""); setDateUntil("");
  };
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (sourceFilter) n++;
    if (pipelineFilter) n++;
    if (tagFilter.length) n++;
    if (dateSince || dateUntil) n++;
    return n;
  }, [sourceFilter, pipelineFilter, tagFilter, dateSince, dateUntil]);

  return (
    <div data-testid="clients-page" className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight inline-flex items-center gap-3">
            <UsersIcon className="h-7 w-7 text-[color:var(--sp-brand)]" />
            {t("nav.clients")}
          </h1>
          <p className="text-[color:var(--sp-text-secondary)] mt-2">{t("clients.countOf", { shown: filtered.length, total: items.length })}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`${base}/tags`} className="sp-btn-ghost inline-flex items-center gap-2 text-sm"><TagIcon className="h-4 w-4" /> {t("clients.cols.tags")}</Link>
          <button onClick={exportCsv} data-testid="clients-export-btn"
                  className="sp-btn-ghost inline-flex items-center gap-2 text-sm">
            <Download className="h-4 w-4" /> {t("clients.export")}
          </button>
          <button onClick={() => setShowImport(true)} data-testid="clients-import-btn"
                  className="sp-btn-ghost inline-flex items-center gap-2 text-sm">
            <Upload className="h-4 w-4" /> {t("clients.import")}
          </button>
          <button data-testid="clients-new-btn" onClick={() => setShowForm(true)} className="sp-btn-primary inline-flex items-center gap-2"><Plus className="h-4 w-4" /> {t("clients.newClient")}</button>
        </div>
      </header>

      {upcomingBirthdays.length > 0 && (
        <div className="sp-card p-4 bg-gradient-to-r from-pink-50 to-amber-50 border-pink-200">
          <div className="flex items-center gap-2 text-sm">
            <Cake className="h-4 w-4 text-pink-600" />
            <strong className="text-pink-900">Compleanni in arrivo:</strong>
            <div className="flex flex-wrap gap-2">
              {upcomingBirthdays.map((c) => (
                <Link key={c.id} to={`/app/clients/${c.id}`} className="text-pink-700 hover:underline text-xs font-bold">
                  {c.name} ({c.days === 0 ? "oggi" : c.days === 1 ? "domani" : `tra ${c.days}gg`})
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search + Sort + Filter button (GHL-style) */}
      <div className="sp-card p-3 flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
          <input data-testid="clients-search-input" placeholder={t("clients.searchPlaceholder")}
                 className="sp-input !pl-10 !py-1.5 text-sm"
                 value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <SortPopover
          fields={SORT_FIELDS}
          field={sortField} dir={sortDir}
          onChange={(f, d) => { setSortField(f); setSortDir(d); }}
          testid="clients-sort"
          defaultField="created_at" defaultDir="desc" />
        <button onClick={() => setShowFilters(true)} data-testid="clients-filter-btn"
                className="sp-btn-ghost inline-flex items-center gap-2 text-sm relative">
          <Filter className="h-4 w-4" /> {t("clients.advancedFilters")}
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-[color:var(--sp-brand)] text-white text-[9px] font-bold flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Bulk actions bar — visible only when selection > 0 */}
      {selectedIds.length > 0 && (
        <BulkActionsBar
          count={selectedIds.length}
          onClear={() => setSelectedIds([])}
          selectedIds={selectedIds}
          tags={tags} pipelineStages={pipelineStages}
          onDone={() => { setSelectedIds([]); load(); }}
        />
      )}

      <div className="sp-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-[color:var(--sp-text-secondary)]">
            {items.length === 0 ? "Nessun cliente ancora. Aggiungine uno per iniziare." : "Nessun risultato per i filtri attuali."}
          </div>
        ) : (
          <div className="sp-table-wrap">
          <table className="w-full">
            <thead className="bg-[color:var(--sp-subtle)] text-left">
              <tr className="label-eyebrow">
                <th className="p-4 w-8">
                  <input type="checkbox"
                    data-testid="clients-select-all"
                    checked={filtered.length > 0 && filtered.every((c) => selectedIds.includes(c.id))}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedIds(filtered.map((c) => c.id));
                      else setSelectedIds([]);
                    }}
                    className="cursor-pointer accent-[color:var(--sp-brand)]" />
                </th>
                <SortableTh field="name" label={t("clients.cols.name")} sortField={sortField} sortDir={sortDir}
                  onSort={(f) => { if (sortField === f) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortField(f); setSortDir("asc"); } }} />
                <th className="p-4">{t("clients.cols.contacts")}</th>
                <th className="p-4">{t("clients.cols.attribution")}</th>
                <SortableTh field="pipeline_stage" label={t("clients.cols.stage")} sortField={sortField} sortDir={sortDir}
                  onSort={(f) => { if (sortField === f) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortField(f); setSortDir("asc"); } }} />
                <th className="p-4">{t("clients.cols.tags")}</th>
                <SortableTh field="created_at" label={t("clients.cols.created")} sortField={sortField} sortDir={sortDir}
                  onSort={(f) => { if (sortField === f) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortField(f); setSortDir("desc"); } }} />
                <SortableTh field="pipeline_updated_at" label={t("clients.cols.lastActivity")} sortField={sortField} sortDir={sortDir}
                  onSort={(f) => { if (sortField === f) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortField(f); setSortDir("desc"); } }} />
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => {
                const stage = pipelineStages.find((s) => s.key === c.pipeline_stage);
                const checked = selectedIds.includes(c.id);
                return (
                  <tr key={c.id} data-testid={`client-row-${c.id}`}
                    className={`${i % 2 ? "bg-[color:var(--sp-bg)]" : ""} ${checked ? "!bg-[color:var(--sp-brand)]/5" : ""} hover:bg-[color:var(--sp-subtle)] transition-colors`}>
                    <td className="p-4">
                      <input type="checkbox" checked={checked}
                        data-testid={`client-select-${c.id}`}
                        onChange={(e) => setSelectedIds((prev) => e.target.checked ? [...prev, c.id] : prev.filter((x) => x !== c.id))}
                        className="cursor-pointer accent-[color:var(--sp-brand)]" />
                    </td>
                    <td className="p-4">
                      <Link to={`${base}/clients/${c.id}`} className="font-bold hover:text-[color:var(--sp-brand)] block">{c.name}</Link>
                      {c.birthday && <div className="text-xs text-[color:var(--sp-text-muted)] mt-0.5 inline-flex items-center gap-1"><Cake className="h-3 w-3" />{new Date(c.birthday).toLocaleDateString(locale())}</div>}
                    </td>
                    <td className="p-4 text-sm text-[color:var(--sp-text-secondary)]">
                      {c.email && <div className="flex items-center gap-2"><Mail className="h-3 w-3" />{c.email}</div>}
                      {c.phone && <div className="flex items-center gap-2 mt-1"><Phone className="h-3 w-3" />{c.phone}</div>}
                    </td>
                    <td className="p-4 text-sm">
                      {c.acquisition_source === "meta_ads" ? (
                        <span data-testid={`client-ads-badge-${c.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)] text-xs font-bold">Lead da Meta Ads</span>
                      ) : (
                        <span className="text-[color:var(--sp-text-muted)]">—</span>
                      )}
                    </td>
                    <td className="p-4 text-xs">
                      {stage ? (
                        <span className="px-2 py-0.5 rounded-full font-bold" style={{ background: `${stage.color}18`, color: stage.color }}>
                          {stage.label}
                        </span>
                      ) : <span className="text-[color:var(--sp-text-muted)]">—</span>}
                    </td>
                    <td className="p-4 text-xs">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).filter((t) => t !== "lead_ads").map((t) => {
                          const color = tagColor(t);
                          return <span key={t} className="px-2 py-0.5 rounded-full font-bold" style={{ background: `${color}18`, color }}>#{t}</span>;
                        })}
                      </div>
                    </td>
                    <td className="p-4 text-xs text-[color:var(--sp-text-secondary)]">
                      {fmtDate(c.created_at)}
                    </td>
                    <td className="p-4 text-xs text-[color:var(--sp-text-secondary)]">
                      {c.pipeline_updated_at || c.updated_at ? relativeTime(c.pipeline_updated_at || c.updated_at) : "—"}
                    </td>
                    <td className="p-4 text-right">
                      <Link data-testid={`client-open-${c.id}`} to={`/app/clients/${c.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-[color:var(--sp-brand)] hover:underline">
                        Apri <ChevronRight className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Modal creazione veloce */}
      {showForm && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="sp-card w-full max-w-lg p-8 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-black">{t("clients.newClient")}</h3>
              <button onClick={() => setShowForm(false)}><X className="h-4 w-4" /></button>
            </div>
            <p className="text-xs text-[color:var(--sp-text-muted)] mb-4">Info essenziali. Per la scheda completa apri il cliente dopo la creazione.</p>
            <form onSubmit={submit} className="space-y-4">
              <div><label className="text-sm font-bold">{t("clients.cols.name")}</label><input data-testid="client-name-input" required placeholder={t("clients.namePlaceholder")} className="sp-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-bold">{t("auth.email")}</label><input data-testid="client-email-input" type="email" placeholder="email@..." className="sp-input mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><label className="text-sm font-bold">{t("clients.phone")}</label><input data-testid="client-phone-input" placeholder="+39 ..." className="sp-input mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div><label className="text-sm font-bold">Compleanno <span className="text-[color:var(--sp-text-muted)] font-normal">(opzionale)</span></label>
                <input data-testid="client-birthday-input" type="date" className="sp-input mt-1" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} />
              </div>
              <div><label className="text-sm font-bold">{t("clients.notes")}</label><textarea data-testid="client-notes-input" placeholder={t("clients.notesPlaceholder")} className="sp-input mt-1 min-h-[60px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <button data-testid="client-submit-btn" className="sp-btn-primary w-full">{t("clients.createClient")}</button>
            </form>
          </div>
        </div>
      )}

      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
      {showFilters && (
        <ClientsFiltersDrawer
          onClose={() => setShowFilters(false)}
          sourceFilter={sourceFilter} setSourceFilter={setSourceFilter}
          pipelineFilter={pipelineFilter} setPipelineFilter={setPipelineFilter}
          tagFilter={tagFilter} setTagFilter={setTagFilter}
          dateField={dateField} setDateField={setDateField}
          dateSince={dateSince} setDateSince={setDateSince}
          dateUntil={dateUntil} setDateUntil={setDateUntil}
          tags={tags} pipelineStages={pipelineStages}
          onReset={resetFilters}
        />
      )}
    </div>
  );
}

function PresetBtn({ onClick, label }) {
  return <button onClick={onClick} className="px-2 py-1 text-[10px] font-bold rounded-md border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]">{label}</button>;
}

// ---------------------------------------------------------------------------
// GHL-style Sort popover
// ---------------------------------------------------------------------------
const SORT_FIELDS = [
  { key: "name", label: "clients.cols.name" },
  { key: "email", label: "auth.email" },
  { key: "phone", label: "clients.phone" },
  { key: "birthday", label: "clients.cols.birthday" },
  { key: "pipeline_stage", label: "clients.pipelineStage" },
  { key: "created_at", label: "clients.cols.createdOn" },
  { key: "updated_at", label: "clients.cols.updatedOn" },
  { key: "pipeline_updated_at", label: "clients.cols.lastActivity" },
];


function SortableTh({ field, label, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <th className="p-4 cursor-pointer hover:bg-[color:var(--sp-subtle)]/60 transition-colors select-none"
      onClick={() => onSort(field)}
      data-testid={`clients-th-${field}`}>
      <div className="inline-flex items-center gap-1">
        {label}
        {active
          ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3 text-[color:var(--sp-brand)]" /> : <ArrowDown className="h-3 w-3 text-[color:var(--sp-brand)]" />)
          : <ArrowUpDown className="h-3 w-3 opacity-30" />}
      </div>
    </th>
  );
}

function BulkActionsBar({ count, selectedIds, onClear, onDone, tags, pipelineStages }) {
  const { t } = useTranslation();
  const [tagOpen, setTagOpen] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const doAction = async (fn) => {
    setBusy(true);
    try {
      await fn();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore azione bulk");
    }
    setBusy(false);
  };

  return (
    <div className="sp-card p-3 flex items-center gap-2 flex-wrap border-[color:var(--sp-brand)]/40 bg-[color:var(--sp-brand)]/5 shadow-md"
      data-testid="clients-bulk-bar">
      <div className="flex items-center gap-2 flex-1 min-w-[200px]">
        <span className="inline-flex items-center justify-center h-6 min-w-[24px] px-1.5 rounded-full bg-[color:var(--sp-brand)] text-white text-xs font-bold">
          {count}
        </span>
        <span className="text-sm font-bold">selezionat{count === 1 ? "o" : "i"}</span>
        <button onClick={onClear} data-testid="clients-bulk-clear-btn"
          className="text-xs text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-text-primary)]">Annulla</button>
      </div>

      {/* Add tag */}
      <div className="relative">
        <button onClick={() => setTagOpen(!tagOpen)} disabled={busy}
          data-testid="clients-bulk-tag-btn"
          className="sp-btn-ghost inline-flex items-center gap-1.5 text-xs">
          <TagIcon className="h-3.5 w-3.5" /> Aggiungi tag <ChevronDown className="h-3 w-3" />
        </button>
        {tagOpen && (
          <div className="absolute top-full mt-2 right-0 z-30 w-56 rounded-xl border border-[color:var(--sp-border)] bg-white shadow-xl p-2 max-h-64 overflow-y-auto"
            data-testid="clients-bulk-tag-menu">
            {tags.length === 0 ? (
              <div className="text-center py-3 text-xs text-[color:var(--sp-text-muted)]">{t("clients.noTagsAvailable")}</div>
            ) : tags.map((t) => (
              <button key={t.name}
                onClick={() => { setTagOpen(false); doAction(() => api.post("/clients/bulk/add-tag", { ids: selectedIds, tag: t.name }).then((r) => toast.success(`Tag «${t.name}» aggiunto a ${r.data.updated} contatti`))); }}
                className="w-full text-left px-2 py-1.5 rounded-md hover:bg-[color:var(--sp-subtle)] text-sm inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: t.color }} />
                {t.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Change stage */}
      <div className="relative">
        <button onClick={() => setStageOpen(!stageOpen)} disabled={busy}
          data-testid="clients-bulk-stage-btn"
          className="sp-btn-ghost inline-flex items-center gap-1.5 text-xs">
          <Move className="h-3.5 w-3.5" /> Cambia fase <ChevronDown className="h-3 w-3" />
        </button>
        {stageOpen && (
          <div className="absolute top-full mt-2 right-0 z-30 w-56 rounded-xl border border-[color:var(--sp-border)] bg-white shadow-xl p-2 max-h-64 overflow-y-auto"
            data-testid="clients-bulk-stage-menu">
            {pipelineStages.map((s) => (
              <button key={s.key}
                onClick={() => { setStageOpen(false); doAction(() => api.post("/clients/bulk/change-stage", { ids: selectedIds, stage: s.key }).then((r) => toast.success(`Fase «${s.label}» applicata a ${r.data.updated} contatti`))); }}
                className="w-full text-left px-2 py-1.5 rounded-md hover:bg-[color:var(--sp-subtle)] text-sm inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Delete */}
      <button onClick={() => {
        if (!confirm(`Eliminare ${count} contatt${count === 1 ? "o" : "i"}? L'azione non è reversibile.`)) return;
        doAction(() => api.post("/clients/bulk/delete", { ids: selectedIds }).then((r) => toast.success(`${r.data.deleted} contatti eliminati`)));
      }} disabled={busy}
        data-testid="clients-bulk-delete-btn"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-red-600 hover:bg-red-50 transition-colors">
        <Trash2 className="h-3.5 w-3.5" /> Elimina
      </button>
    </div>
  );
}

// Small helper: relative time in italian
function relativeTime(iso) {
  const t = new Date(iso).getTime();
  if (!t) return "—";
  const diff = Date.now() - t;
  const min = Math.round(diff / 60000);
  if (min < 1) return "adesso";
  if (min < 60) return `${min} min fa`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h fa`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} g fa`;
  const m = Math.round(d / 30);
  if (m < 12) return `${m} mesi fa`;
  return `${Math.round(m / 12)} anni fa`;
}

function daysAgoIso(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function ImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [defaultSource, setDefaultSource] = useState("import");
  const [defaultTags, setDefaultTags] = useState("");
  const inputRef = useRef(null);

  const CRM_FIELDS = [
    { value: "", label: "— Ignora —" },
    { value: "name", label: "Nome" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Telefono" },
    { value: "birthday", label: "Compleanno (YYYY-MM-DD)" },
    { value: "source", label: "Origine" },
    { value: "pipeline_stage", label: "Fase pipeline" },
    { value: "tags", label: "Tag (comma-separated)" },
    { value: "notes", label: "Note" },
  ];

  const autoGuess = (h) => {
    const l = h.toLowerCase();
    if (l.includes("nome") || l === "name" || l.includes("full")) return "name";
    if (l.includes("mail")) return "email";
    if (l.includes("tel") || l.includes("phone") || l.includes("cell")) return "phone";
    if (l.includes("compleanno") || l.includes("birth")) return "birthday";
    if (l.includes("tag")) return "tags";
    if (l.includes("note")) return "notes";
    if (l.includes("origine") || l.includes("source")) return "source";
    return "";
  };

  const onFile = async (f) => {
    setFile(f); setPreview(null); setResult(null);
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/clients/import/preview", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      const guess = {};
      (r.data.headers || []).forEach((h) => { guess[h] = autoGuess(h); });
      setMapping(guess);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV non valido");
    }
  };

  const doImport = async () => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mapping_json", JSON.stringify(mapping));
    fd.append("default_source", defaultSource);
    fd.append("default_tags", defaultTags);
    try {
      const r = await api.post("/clients/import", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`${r.data.created} creati · ${r.data.updated} aggiornati`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore import");
    } finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose} data-testid="clients-import-modal">
      <div className="sp-card w-full max-w-2xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl font-black">Importa contatti da CSV</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        {!preview && (
          <>
            <p className="text-sm text-[color:var(--sp-text-secondary)] mb-4">
              Carica un file CSV. Nella schermata dopo potrai mappare le colonne ai campi del CRM.
              Dedup automatico su email o telefono.
            </p>
            <input ref={inputRef} type="file" accept=".csv,text/csv"
                   onChange={(e) => onFile(e.target.files?.[0])}
                   data-testid="import-file-input"
                   className="block w-full text-sm border border-[color:var(--sp-border)] rounded-lg p-2" />
          </>
        )}

        {preview && !result && (
          <div className="space-y-4">
            <div className="text-sm text-[color:var(--sp-text-secondary)]">
              <b>{preview.headers.length}</b> colonne rilevate.
              Mappa ogni colonna al campo CRM corrispondente:
            </div>
            <div className="max-h-72 overflow-y-auto space-y-2" data-testid="import-mapping-list">
              {preview.headers.map((h) => (
                <div key={h} className="flex items-center gap-2">
                  <div className="text-sm font-bold w-1/2 truncate">{h}</div>
                  <select value={mapping[h] || ""}
                          onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                          className="sp-input flex-1" data-testid={`import-map-${h}`}>
                    {CRM_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold">Tag da assegnare (opzionale, virgola)</label>
                <input value={defaultTags} onChange={(e) => setDefaultTags(e.target.value)}
                       placeholder="import_2026,newsletter" className="sp-input mt-1"
                       data-testid="import-default-tags" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPreview(null)} className="sp-btn-ghost">Cambia file</button>
              <button onClick={doImport} disabled={importing} data-testid="import-run-btn"
                      className="sp-btn-primary">
                {importing ? "Importo…" : "Importa"}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="import-result">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4">
              <div className="text-sm font-bold text-emerald-800">Import completato!</div>
              <div className="text-sm text-emerald-700 mt-1">
                <b>{result.created}</b> nuovi ·
                <b> {result.updated}</b> aggiornati ·
                <b> {result.skipped}</b> saltati
              </div>
            </div>
            {result.errors && result.errors.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-amber-700 font-bold">
                  {result.errors.length} errori
                </summary>
                <ul className="mt-1 pl-4 list-disc space-y-0.5 text-amber-700">
                  {result.errors.slice(0, 20).map((e, i) => (
                    <li key={i}>Riga {e.row}: {e.error}</li>
                  ))}
                </ul>
              </details>
            )}
            <div className="flex justify-end">
              <button onClick={onDone} className="sp-btn-primary">Chiudi e ricarica</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ClientsFiltersDrawer({
  onClose, sourceFilter, setSourceFilter, pipelineFilter, setPipelineFilter,
  tagFilter, setTagFilter, dateField, setDateField, dateSince, setDateSince,
  dateUntil, setDateUntil, tags, pipelineStages, onReset,
}) {
  const { t } = useTranslation();
  const toggleTag = (name) => {
    setTagFilter((p) => p.includes(name) ? p.filter((t) => t !== name) : [...p, name]);
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose}
         data-testid="clients-filters-drawer">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto p-6 shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-black">{t("clients.filters")}</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">{t("clients.pipelineStage")}</label>
            <select value={pipelineFilter} onChange={(e) => setPipelineFilter(e.target.value)}
                    className="sp-input mt-1" data-testid="clients-pipeline-filter">
              <option value="">{t("clients.allStages")}</option>
              {pipelineStages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">{t("clients.cols.tags")}</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {tags.length === 0 && <div className="text-xs text-[color:var(--sp-text-muted)]">{t("clients.noTags")}</div>}
              {tags.map((t) => {
                const on = tagFilter.includes(t.name);
                return (
                  <button key={t.id} onClick={() => toggleTag(t.name)}
                    data-testid={`tag-filter-${t.name}`}
                    className="px-2 py-1 rounded-full text-[10px] font-bold border"
                    style={on
                      ? { background: t.color, borderColor: t.color, color: "#fff" }
                      : { borderColor: `${t.color}55`, background: `${t.color}10`, color: t.color }}>
                    {on ? "✓ " : ""}#{t.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider">{t("clients.dateRange")}</label>
            <select value={dateField} onChange={(e) => setDateField(e.target.value)}
                    className="sp-input mt-1 !py-1.5 !text-sm" data-testid="clients-date-field">
              <option value="created_at">{t("clients.dateFields.created")}</option>
              <option value="updated_at">{t("clients.dateFields.updated")}</option>
              <option value="pipeline_updated_at">{t("clients.dateFields.pipeline")}</option>
            </select>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <input type="date" value={dateSince} max={dateUntil || undefined}
                     onChange={(e) => setDateSince(e.target.value)}
                     className="sp-input !py-1.5 !text-sm" data-testid="clients-date-since" />
              <input type="date" value={dateUntil} min={dateSince || undefined}
                     onChange={(e) => setDateUntil(e.target.value)}
                     className="sp-input !py-1.5 !text-sm" data-testid="clients-date-until" />
            </div>
            <div className="mt-2 flex gap-1">
              <PresetBtn onClick={() => { setDateSince(daysAgoIso(7)); setDateUntil(daysAgoIso(0)); }} label={t("clients.ranges.7d")} />
              <PresetBtn onClick={() => { setDateSince(daysAgoIso(30)); setDateUntil(daysAgoIso(0)); }} label={t("clients.ranges.30d")} />
              <PresetBtn onClick={() => { setDateSince(daysAgoIso(90)); setDateUntil(daysAgoIso(0)); }} label={t("clients.ranges.90d")} />
            </div>
          </div>
        </div>
        <div className="flex justify-between mt-6">
          <button onClick={onReset} className="sp-btn-ghost text-sm" data-testid="clients-filters-reset">{t("common.reset")}</button>
          <button onClick={onClose} className="sp-btn-primary" data-testid="clients-filters-close">{t("common.close")}</button>
        </div>
      </div>
    </div>
  );
}

